import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { ItemStats } from '@/features/items/components/ItemStats'
import { PatientCard } from '@/features/patients/components/PatientCard'
import { PatientItemsSection } from '@/features/patients/components/sections/PatientItemsSection'
import { PatientItems } from '@/features/patients/types/patient'
import { Monitor, UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bed } from '../types/bed'
import { PatientToBedSection } from './sections/PatientToBedSection'

export function BedDetailContent({ bed, patientItems }: { bed: Bed; patientItems?: PatientItems }) {
  const { t } = useTranslation()

  const [activeSection, setActiveSection] = useState<'managePatient' | 'manageItem' | null>(null)
  const patient = patientItems?.patient
  const items = patientItems?.items ?? []
  const hasPatient = !!patient
  const hasItems = items.length > 0

  return (
    <>
      <SectionCard title={`${t('common.bed')} ${bed?.name}`}>
        <div className="flex flex-col gap-5">
          {!hasPatient && !hasItems ? (
            <p className="py-8 text-center text-xs text-muted-foreground">{t('bed.detail.noPatientAndEquipment')}</p>
          ) : (
            <>
              {hasPatient ? (
                <>
                  <div className="text-sm mb-1">{t('common.patient')}</div>
                  <PatientCard patient={patient} variant="summary" isLoading={false}></PatientCard>
                  {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-border/30 bg-secondary/30 p-3">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row gap-2">
                        <UserIcon className="size-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">{patient?.fullName}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{}</span>
                    </div>
                  </div> */}
                </>
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">{t('bed.detail.noPateint')}</p>
              )}

              {hasItems ? (
                <>
                  <div className="text-sm mb-1">{t('common.equipment')}</div>
                  <div className="flex flex-col gap-3" data-testid="bed-detail-assigned-equipment-list">
                    {items.map(item => {
                      const title = item.productName || `${item.productBrand} ${item.productModel}`
                      return (
                        <ItemStats
                          key={item.id}
                          title={title}
                          subtitle={item.itemSerialNumber}
                          days={item.daysSinceLastStatusChange}
                        ></ItemStats>
                      )
                    })}
                  </div>
                </>
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">{t('bed.detail.noEquipment')}</p>
              )}
            </>
          )}
        </div>
      </SectionCard>

      {/* Action Buttons */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button
          variant={activeSection === 'managePatient' ? 'default' : 'outline'}
          className="h-12 flex-col gap-1 text-xs"
          data-testid="bed-detail-manage-patient"
          onClick={() => setActiveSection(activeSection === 'managePatient' ? null : 'managePatient')}
        >
          <UserIcon className="size-4" />
          {t('bed.detail.managePatient')}
        </Button>
        <Button
          variant={activeSection === 'manageItem' ? 'default' : 'outline'}
          className="h-12 flex-col gap-1 text-xs"
          data-testid="bed-detail-manage-item"
          onClick={() => setActiveSection(activeSection === 'manageItem' ? null : 'manageItem')}
        >
          <Monitor className="size-4" />
          {t('patient.detail.manageEquipment')}
        </Button>
      </div>

      {/* Sections */}
      {activeSection === 'managePatient' && (
        <PatientToBedSection departmentId={bed.departmentId} bed={bed} patient={patient}></PatientToBedSection>
      )}
      {activeSection === 'manageItem' && (
        <PatientItemsSection departmentId={bed.departmentId} patientItems={patientItems}></PatientItemsSection>
      )}
    </>
  )
}
